// Progressive enhancement for the dl-reviews slider: Yotpo only syncs the
// aggregate score to Shopify, so live review text is fetched client-side and
// swapped in over the server-rendered curated fallback slides.

const SECTION_SELECTOR = "[data-yotpo-reviews]";
const SLIDER_SELECTOR = "[data-blaze-slider]";
const TRACK_SELECTOR = ".blaze-track";
const ROOT_MARGIN = "500px";
const SITE_REVIEWS_ID = "yotpo_site_reviews";
const STAR_PATH =
  "M12 17.75l-6.172 3.245 1.179-6.873-4.993-4.867 6.9-1.002L12 2l3.086 6.253 6.9 1.002-4.993 4.867 1.179 6.873z";

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const RELATIVE_UNITS = [
  ["year", 365 * 24 * 60 * 60 * 1000],
  ["month", 30 * 24 * 60 * 60 * 1000],
  ["week", 7 * 24 * 60 * 60 * 1000],
  ["day", 24 * 60 * 60 * 1000],
  ["hour", 60 * 60 * 1000],
  ["minute", 60 * 1000],
];

// "3 days ago" style, to match the free-text dates used in the curated blocks.
const relativeDate = (iso) => {
  if (!iso) {
    return "";
  }

  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) {
    return "";
  }

  const diff = then - Date.now();
  const abs = Math.abs(diff);

  try {
    const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
    for (const [unit, ms] of RELATIVE_UNITS) {
      if (abs >= ms || unit === "minute") {
        return formatter.format(Math.round(diff / ms), unit);
      }
    }
    return formatter.format(0, "day");
  } catch {
    return new Date(then).toLocaleDateString();
  }
};

const starRow = (score) => {
  const clamped = Math.max(0, Math.min(5, Number(score) || 0));
  const fill = ((clamped / 5) * 100).toFixed(2);
  const label = `Rated ${Math.round(clamped * 10) / 10} out of 5`;
  const stars = Array.from(
    { length: 5 },
    () =>
      `<span class="star-rating__star"><svg viewBox="0 0 24 24" fill="currentColor"><path d="${STAR_PATH}" /></svg></span>`,
  ).join("");

  return `<div class="star-rating flex align-center g-2">
    <div class="star-rating__stars" style="--star-size: 20px;" role="img" aria-label="${escapeHtml(label)}">
      <div class="star-rating__icons" aria-hidden="true">${stars}</div>
      <div class="star-rating__clip" style="width: ${fill}%;" aria-hidden="true"><div class="star-rating__icons">${stars}</div></div>
    </div>
  </div>`;
};

const reviewSlide = (review, palette) => {
  const title = escapeHtml(review.title);
  const author = escapeHtml(review.author);
  const dateText = escapeHtml(review.dateText);
  const body = escapeHtml(review.body).replace(/\r?\n/g, "<br>");

  return `<div class="dl-reviews__slide h-auto">
    <article class="dl-review-card border-radius-large flex flex-col" style="--card-bg: ${palette.cardBg}; --card-text: ${palette.cardText}; --star: ${palette.star};">
      <div class="dl-review-card__head flex flex-between align-center g-2">
        ${starRow(review.score)}
        ${dateText ? `<span class="dl-review-card__date">${dateText}</span>` : ""}
      </div>
      ${title ? `<h3 class="dl-review-card__title uppercase">${title}</h3>` : ""}
      ${body ? `<div class="dl-review-card__body">${body}</div>` : ""}
      ${author ? `<p class="dl-review-card__author">${author}</p>` : ""}
    </article>
  </div>`;
};

const normaliseReview = (raw) => ({
  score: Number(raw.score) || 0,
  title: raw.title || "",
  body: raw.content || "",
  author: (raw.user && (raw.user.display_name || raw.user.name)) || raw.name || "",
  dateText: relativeDate(raw.created_at),
});

const fetchYotpoReviews = async ({ appKey, productId, max }) => {
  const product = encodeURIComponent(productId || SITE_REVIEWS_ID);
  const perPage = Math.max(1, Math.min(100, max * 3));
  const url = `https://api-cdn.yotpo.com/v1/widget/${encodeURIComponent(
    appKey,
  )}/products/${product}/reviews.json?per_page=${perPage}&page=1&sort=date`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Yotpo request failed with status ${response.status}`);
  }

  const data = await response.json();
  const reviews =
    data && data.response && Array.isArray(data.response.reviews) ? data.response.reviews : [];
  return reviews.map(normaliseReview);
};

const getBlazeController = async () => {
  if (window.themeBlazeSliderController) {
    return window.themeBlazeSliderController;
  }
  const { blazeSliderController } = await import("@theme/blaze-slider-controller");
  return blazeSliderController;
};

const reinitSlider = async (sliderEl) => {
  const controller = await getBlazeController();
  if (!controller) {
    return;
  }
  // The slider already initialised over the curated slides; rebuild over the new ones.
  controller.destroy(sliderEl);
  controller.initSlider(sliderEl);
};

class YotpoReviews {
  constructor(section) {
    this.section = section;
    this.slider = section.querySelector(SLIDER_SELECTOR);
    this.track = this.slider ? this.slider.querySelector(TRACK_SELECTOR) : null;

    this.config = {
      appKey: (section.dataset.yotpoAppKey || "").trim(),
      productId: (section.dataset.yotpoProductId || "").trim(),
      max: Number(section.dataset.yotpoMax) || 9,
      minRating: Number(section.dataset.yotpoMinRating) || 0,
    };

    this.palette = {
      star: section.dataset.yotpoStarColor || "var(--brand-color-7)",
      cardBg: section.dataset.yotpoCardBg || "#FBF6EF",
      cardText: section.dataset.yotpoCardText || "#282047",
    };

    if (!this.slider || !this.track || !this.config.appKey) {
      return;
    }

    this.initObserver();
  }

  initObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }
        this.observer.disconnect();
        this.load();
      },
      { rootMargin: `${ROOT_MARGIN} 0px ${ROOT_MARGIN}` },
    );

    this.observer.observe(this.section);
  }

  async load() {
    let reviews;
    try {
      reviews = await fetchYotpoReviews(this.config);
    } catch (error) {
      // Leave the curated fallback slides in place.
      console.warn("Yotpo reviews unavailable", error);
      return;
    }

    const filtered = reviews
      .filter((review) => review.body && review.score >= this.config.minRating)
      .slice(0, this.config.max);

    if (!filtered.length) {
      return;
    }

    this.track.innerHTML = filtered.map((review) => reviewSlide(review, this.palette)).join("");
    await reinitSlider(this.slider);
  }
}

const initYotpoReviews = () => {
  document.querySelectorAll(SECTION_SELECTOR).forEach((section) => new YotpoReviews(section));
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initYotpoReviews);
} else {
  initYotpoReviews();
}
